import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'

const useLayoverDurationFilterManager = (journeyLeg: 0 | 1 = 0) => {
    const t = useTranslations('common')
    const { triggerSrpEvent } = useClevertapForSrp()
    const updateCache = useUpdateSearchResultCache()
    const { filterData, selectedJourneyLeg } = useSearchResults(journeyLeg)
    const targetLayoverDuration = filterData?.layoverDuration?.[selectedJourneyLeg]
    const { min: defaultMin, max: defaultMax, current } = targetLayoverDuration || {}
    const { min: currentMin, max: currentMax } = current || {}

    const title = t('layoverDuration')

    const changeLayoverDurationRange = useCallback(
        (min: number, max: number) => {
            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'layover_duration',
                action_value: `${min}-${max}`
            })

            updateCache(currentData => {
                if (currentData?.filterData?.layoverDuration?.[selectedJourneyLeg]) {
                    currentData.filterData.layoverDuration[selectedJourneyLeg].current = { min, max }
                }
                return currentData as NonNullable<typeof currentData>
            })
        },
        [selectedJourneyLeg, triggerSrpEvent, updateCache]
    )

    const handleReset = useCallback(() => {
        const numDefaultMin = Number(defaultMin)
        const numDefaultMax = Number(defaultMax)
        if (!isNaN(numDefaultMin) && !isNaN(numDefaultMax)) {
            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'reset',
                action_value: 'layover_duration'
            })

            updateCache(currentData => {
                if (currentData?.filterData?.layoverDuration?.[selectedJourneyLeg]) {
                    currentData.filterData.layoverDuration[selectedJourneyLeg].current = {
                        min: numDefaultMin,
                        max: numDefaultMax
                    }
                }
                return currentData as NonNullable<typeof currentData>
            })
        }
    }, [defaultMax, defaultMin, selectedJourneyLeg, triggerSrpEvent, updateCache])

    const isLayoverDurationFilterApplied = useMemo(() => {
        return currentMin !== defaultMin || currentMax !== defaultMax
    }, [currentMax, currentMin, defaultMax, defaultMin])

    return {
        title,
        min: defaultMin,
        max: defaultMax,
        currentMin,
        currentMax,
        changeLayoverDurationRange,
        handleReset,
        isLayoverDurationFilterApplied
    }
}

export default useLayoverDurationFilterManager
